import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, of, switchMap } from 'rxjs';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { AuthService } from '@app/services/auth/auth.service';
import { SubdominioService } from '@app/services/general/subdominio.service';
import { RoutesUrl } from '@app/routes.enum';

/**
 * Guardián funcional para proteger rutas que requieren autenticación.
 * Si no hay 'usuario' en localStorage (ej. primera vez en este subdominio),
 * o si el rol cacheado no corresponde al subdominio actual (localStorage es
 * por-origen y puede quedar desfasado tras un switch de contexto que redirige
 * a otro subdominio), rehidrata la sesión desde la cookie — compartida entre
 * subdominios — antes de mandar a login.
 */
export const authGuard: CanActivateFn = (route) => {
  const servicioSesion = inject(ServicioSesion);
  const authService = inject(AuthService);
  const subdominioService = inject(SubdominioService);
  const router = inject(Router);

  // Venimos de un switchContext que cruzó de subdominio: no confiar en el localStorage
  // de este origen (puede ser de una visita vieja), forzar refresco contra el server.
  const vieneDeSwitch = route.queryParamMap.has('ctxSwitch');

  if (!vieneDeSwitch && servicioSesion.haySesionActiva() && servicioSesion.contextoActivoCoincideConSlug(subdominioService.slugActual())) {
    return true;
  }

  return authService.getMe().pipe(
    switchMap((res) => {
      servicioSesion.guardarSesion(res.user);
      servicioSesion.guardarContextos(res.contextos);
      if (!res.contexto_activo) {
        return of(true);
      }

      // getMe() nunca manda permisos (a propósito, ver UserResource): hay que
      // pedirlos al backend para el contexto activo real vía switchContext,
      // igual que hace el selector de rol — si no, quedan vacíos hasta que el
      // usuario abre el selector y cambia de rol manualmente para refrescarlos.
      return servicioSesion.switchContext(res.contexto_activo).pipe(
        map(() => {
          const rolActivo = servicioSesion.contextoArolActivo(res.contexto_activo!);

          // Si el contexto real (server) corresponde a otro subdominio (ej. se
          // llegó por URL directa a uno viejo), redirige al correcto en vez de
          // dejar pasar la navegación aquí mismo.
          const slugEsperado = subdominioService.slugEsperadoParaRol(rolActivo);
          if (slugEsperado && slugEsperado !== subdominioService.slugActual()) {
            subdominioService.irASlug(slugEsperado);
            return false;
          }

          return true;
        })
      );
    }),
    // Si getMe() o el switchContext fallan, `guardarSesion`/`guardarContextos` ya pudieron
    // haber corrido antes del error (ej. el contexto activo de la cookie ya no existe tras
    // restaurar la BD de otro ambiente) — sin este cleanup, localStorage queda marcado como
    // "hay sesión" y sinSesionGuard() rebota de vuelta a /main sin validar nada, generando
    // un loop infinito entre ambos guards. Se limpia siempre antes de mandar a login.
    catchError(() => {
      servicioSesion.cerrarSesion();
      return of(router.createUrlTree(['/', RoutesUrl.LOGIN]));
    })
  );
};

